import { TokenType } from "../tokenizer.js";
import { isCharacterSet, isColumnKeywordValue, isCommaToken, isConstraintState, isLeftParentToken, isRightBracketToken, isRightParentToken, isSemicolonToken, requote, toStringLiteral } from "../helper.js";
//#region ../schema-sql-parser/dist/parser/statement/default.expression.js
var QuotedKinds = {
	"'": "string",
	"[": "bracket",
	"\"": "word",
	"`": "word"
};
var ExpressionWords = [
	"AND",
	"AT",
	"BETWEEN",
	"COLLATE",
	"ILIKE",
	"IN",
	"IS",
	"LIKE",
	"NOT",
	"OR",
	"OVERLAPS",
	"SIMILAR"
];
var VendorColumnWords = [
	"COLUMN_FORMAT",
	"INDEX",
	"MASK",
	"ROWGUIDCOL",
	"SPARSE",
	"STORAGE"
];
var NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
var TYPE_WORD = /^(?:[a-z_][\w$]*|"(?:[^"]|"")*")$/i;
var ARRAY_DIMENSION = /^\[\d*\]$/;
var word = (token) => token && token.type === TokenType.string && !token.quoted ? token.value.toUpperCase() : "";
var isNextValueFor = (tokens) => (pos) => word(tokens[pos]) === "FOR" && word(tokens[pos - 1]) === "VALUE" && word(tokens[pos - 2]) === "NEXT";
var matchDefaultExpression = (tokens) => {
	const isComma = isCommaToken(tokens);
	const isLeftParent = isLeftParentToken(tokens);
	const isRightParent = isRightParentToken(tokens);
	const isRightBracket = isRightBracketToken(tokens);
	const isSemicolon = isSemicolonToken(tokens);
	const isColumnKeyword = isColumnKeywordValue(tokens);
	const constraintState = isConstraintState(tokens);
	const characterSet = isCharacterSet(tokens);
	const nextValueFor = isNextValueFor(tokens);
	const isTimeZone = (pos) => {
		if (word(tokens[pos]) === "LOCAL") return isTimeZone(pos + 1);
		return word(tokens[pos]) === "TIME" && word(tokens[pos + 1]) === "ZONE";
	};
	const endsAt = (pos) => {
		const keyword = word(tokens[pos]);
		if (keyword === "FOR") return !nextValueFor(pos);
		if (keyword === "WITH" || keyword === "WITHOUT") return !isTimeZone(pos + 1);
		if (keyword === "TAG") return isLeftParent(pos + 1);
		return isColumnKeyword(pos) || VendorColumnWords.includes(keyword) || constraintState(pos) || characterSet(pos);
	};
	const isCase = (pos) => word(tokens[pos]) === "CASE";
	const isEnd = (pos) => /^END(?:::|$)/.test(word(tokens[pos]));
	return (pos) => {
		let depth = 0;
		let cursor = pos;
		for (; cursor < tokens.length && !isSemicolon(cursor); cursor++) if (isLeftParent(cursor) || isCase(cursor)) depth++;
		else if (isRightParent(cursor)) {
			if (depth === 0) break;
			depth--;
		} else if (depth > 0) {
			if (isEnd(cursor)) depth--;
		} else if (isComma(cursor) || isRightBracket(cursor)) break;
		else if (cursor > pos && endsAt(cursor)) break;
		return cursor - pos;
	};
};
var toPieces = (tokens, database) => tokens.flatMap((token) => {
	switch (token.type) {
		case TokenType.leftParent: return [{
			kind: "open",
			text: "("
		}];
		case TokenType.rightParent: return [{
			kind: "close",
			text: ")"
		}];
		case TokenType.comma: return [{
			kind: "comma",
			text: ","
		}];
		case TokenType.period: return [{
			kind: "period",
			text: "."
		}];
	}
	if (token.quoted) return [{
		kind: QuotedKinds[token.quoted],
		text: requote(token, database),
		token
	}];
	return token.value.split("::").flatMap((part, index) => [...index ? [{
		kind: "cast",
		text: "::"
	}] : [], ...part ? [{
		kind: "word",
		text: part
	}] : []]);
});
var isOperator = (piece) => piece.kind === "word" && !/[\p{L}\p{N}_$#@'"`]/u.test(piece.text);
var isSubscript = (prev, next) => /^ARRAY$/i.test(prev.text) || !/^\[[\p{L}_@#]/u.test(next.text);
var isSplitOperator = (prev, next) => prev.kind === "word" && next.kind === "word" && (next.text === "=" && /[<>!=]$/.test(prev.text) || prev.text === "=" && next.text.startsWith(">"));
var spaceBetween = (prev, next) => {
	if ([
		"close",
		"comma",
		"period",
		"cast"
	].includes(next.kind)) return false;
	if ([
		"open",
		"period",
		"cast"
	].includes(prev.kind)) return false;
	if (prev.kind === "comma") return true;
	if (isSplitOperator(prev, next)) return false;
	if (next.kind === "bracket" && !isSubscript(prev, next)) return true;
	if (next.kind === "open" || next.kind === "bracket") return isOperator(prev);
	return true;
};
var render = (pieces) => pieces.reduce((text, piece, index) => index && spaceBetween(pieces[index - 1], piece) ? `${text} ${piece.text}` : text + piece.text, "");
var closeOf = (pieces, start) => {
	let depth = 0;
	for (let index = start; index < pieces.length; index++) if (pieces[index].kind === "open") depth++;
	else if (pieces[index].kind === "close" && --depth === 0) return index;
	return -1;
};
var unwrap = (pieces) => {
	let inner = pieces;
	while (inner.length > 1 && inner[0].kind === "open" && closeOf(inner, 0) === inner.length - 1) inner = inner.slice(1, -1);
	return inner;
};
var isTypeName = (pieces) => {
	let depth = 0;
	return pieces.length > 0 && pieces[0].kind === "word" && pieces.every(({ kind, text }) => {
		if (kind === "open") {
			depth++;
			return true;
		}
		if (kind === "close") return --depth >= 0;
		if (depth > 0 || kind === "period") return true;
		if (kind === "bracket") return ARRAY_DIMENSION.test(text);
		return kind === "word" && TYPE_WORD.test(text) && !ExpressionWords.includes(text.toUpperCase());
	});
};
var isLiteral = (pieces) => {
	if (pieces.length === 1 && pieces[0].kind === "string") return true;
	const text = render(pieces);
	return NUMBER.test(text) || /^(?:null|true|false)$/i.test(text);
};
var lastCastOf = (pieces) => {
	let depth = 0;
	let cast = -1;
	pieces.forEach(({ kind }, index) => {
		if (kind === "open") depth++;
		else if (kind === "close") depth--;
		else if (depth === 0 && kind === "cast") cast = index;
	});
	return cast;
};
var uncast = (pieces) => {
	const cast = lastCastOf(pieces);
	if (cast === -1 || !isTypeName(pieces.slice(cast + 1))) return pieces;
	const operand = unwrap(pieces.slice(0, cast));
	return isLiteral(operand) ? operand : pieces;
};
function writeDefaultExpression(tokens, database) {
	const pieces = uncast(unwrap(toPieces(tokens, database)));
	const [first] = pieces;
	if (pieces.length === 1 && first.token) {
		const { value, prefix = "" } = first.token;
		return prefix + toStringLiteral(value, database);
	}
	return render(pieces);
}
function defaultExpressionParser(tokens, $pos, database) {
	const span = matchDefaultExpression(tokens)($pos.value);
	const expression = tokens.slice($pos.value, $pos.value + span);
	$pos.value += span;
	return writeDefaultExpression(expression, database);
}
var isNextvalName = ({ kind, text, token }) => kind === "word" && (token ? token.quoted === "\"" && token.value === "nextval" : /^nextval$/i.test(text));
var SEQUENCE_PREFIX = /^(?:e|u&)?$/i;
var isSequenceArgument = (pieces) => {
	const operand = unwrap(pieces);
	const cast = lastCastOf(operand);
	if (cast !== -1) return isTypeName(operand.slice(cast + 1)) && isSequenceArgument(operand.slice(0, cast));
	const [name] = operand;
	return operand.length === 1 && name.kind === "string" && SEQUENCE_PREFIX.test(name.token?.prefix ?? "");
};
var isNextvalDefault = (tokens) => {
	const defaultExpression = matchDefaultExpression(tokens);
	return (pos) => {
		const pieces = unwrap(toPieces(tokens.slice(pos, pos + defaultExpression(pos))));
		const [name, open] = pieces;
		return !!name && isNextvalName(name) && open?.kind === "open" && closeOf(pieces, 1) === pieces.length - 1 && isSequenceArgument(pieces.slice(2, -1));
	};
};
//#endregion
export { defaultExpressionParser, isNextValueFor, isNextvalDefault, matchDefaultExpression, writeDefaultExpression };
